import { FRENCH_DEPARTMENTS } from '@omecare/utils';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerForm } from '../datepicker/datepicker-form';
import type { DoctorProfileUseForm } from './use-doctor-profile-form';

/** `thesis` select value of a substitute who has not defended their thesis. */
const NO_THESIS_VALUE = 'nothesis';

/**
 * Label of a département in the council select, e.g. `69 – Rhône`.
 *
 * @param department - a catalog entry
 * @returns the option label
 */
export const formatDepartmentOption = (department: {
  code: string;
  label: string;
}): string => `${department.code} – ${department.label}`;

/**
 * Whether the licence de remplacement fields apply: only a non-thésé
 * substitute holds one. The values are kept when the thesis flips; the
 * fields are merely hidden.
 *
 * @param thesis - the current `thesis` select value
 * @returns `true` when the licence fields must be shown
 */
export const requiresLicenceFields = (
  thesis: string | null | undefined,
): boolean => thesis === NO_THESIS_VALUE;

/**
 * Contract-related profile of a substitute (issue #796): URSSAF number for
 * everyone, licence number / expiry / delivering council for a non-thésé.
 * Every field is optional: existing profiles are never blocked. Rendered
 * inside the "Profil contrat" `FormSection`, which owns the title.
 *
 * @param props.form - the unified profile form
 * @returns the section's fields
 */
export const ContractProfileFields = ({
  form,
}: {
  form: DoctorProfileUseForm;
}) => {
  const { t } = useTranslation();
  const thesis = form.watch('thesis');
  const showLicence = requiresLicenceFields(thesis);

  return (
    <>
      <FormField
        control={form.control}
        name="urssafNumber"
        render={({ field }) => (
          <FormItem className="grid gap-2">
            <FormLabel>
              {t('edit-user-profile-form.urssaf-number.label')}
            </FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder=""
                {...field}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {showLicence && (
        <>
          <FormField
            control={form.control}
            name="licenceNumber"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  {t('edit-user-profile-form.licence-number.label')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder=""
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DatePickerForm
            form={form}
            fieldName="licenceExpiresAt"
            label={t('edit-user-profile-form.licence-expires-at.label')}
            captionLayout="dropdown"
          />

          <FormField
            control={form.control}
            name="licenceCouncilDepartmentCode"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>
                  {t('edit-user-profile-form.licence-council.label')}
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? undefined}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          'edit-user-profile-form.licence-council.placeholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {FRENCH_DEPARTMENTS.map((department) => (
                      <SelectItem
                        key={department.code}
                        value={department.code}
                      >
                        {formatDepartmentOption(department)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </>
  );
};
